import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Beaker } from 'lucide-react';
import { getEnabledModes } from '../modes';
import { LabStation } from './LabStation';
import { FamilyPicker } from './FamilyPicker';
import { ChallengeAcceptModal } from './modals/ChallengeAcceptModal';
import { ModeIntroSheet } from './modals/ModeIntroSheet';
import { FluencyModulePicker } from './modals/FluencyModulePicker';
import { PageContainer } from '../components/shared/PageContainer';
import { itemVariants } from '../components/shared/animations';
import { WorldZone } from '../components/shared/WorldZone';
import { useDjaySelection, getDjayLabWelcome } from '../mascot';
import { useAppStore } from '../app/hooks/useAppStore';
import { getFamilyMasteryKey } from '../mastery';
import { ConfettiCelebration } from '../accounts/components/ConfettiCelebration';
import { ContinueStrip } from './shared/ContinueStrip';
import { NewlyUnlockedBanner } from './shared/NewlyUnlockedBanner';
import { LabHero } from './shared/LabHero';
import { useLabPersonalization } from './hooks/useLabPersonalization';
import { useLabEvidence } from './hooks/useLabEvidence';
import { useModeFlow, type StartModeConfig } from './hooks/useModeFlow';
import { useLabData } from './hooks/useLabData';
// ─── Types ────────────────────────────────────────────────────
interface LearningLabPageProps {
  onStartMode?: (modeId: string, config?: StartModeConfig) => void;
}
// ─── Component ────────────────────────────────────────────────
export function LearningLabPage({
  onStartMode
}: LearningLabPageProps) {
  const allModes = getEnabledModes();
  const {
    selectedVariant
  } = useDjaySelection();
  const {
    state,
    selectedGroup
  } = useAppStore();
  // ── Data Hook ──
  const {
    lastSession,
    newlyUnlockedFamilyId,
    setNewlyUnlockedFamilyId,
    showCelebration,
    personalRecords
  } = useLabData();
  const labWelcome = useMemo(() => getDjayLabWelcome(lastSession), [lastSession]);
  // ── Personalization Hook ──
  const {
    isFluencyColdStart,
    getEnrichmentForMode
  } = useLabPersonalization(personalRecords);
  // ── Flow Hook ──
  const {
    familyPickerOpen,
    familyPickerMode,
    challengeModalOpen,
    pendingChallengeFamilyId,
    introSheetMode,
    fluencyModulePickerOpen,
    handleModeClick,
    handleNewlyUnlockedClick,
    handleFamilySelect,
    handleFamilyPickerClose,
    handleChallengeAccept,
    handleChallengeDecline,
    handleIntroStart,
    handleIntroCancel,
    setFluencyModulePickerOpen
  } = useModeFlow({
    onStartMode,
    newlyUnlockedFamilyId
  });
  // ── Bewijsniveau ──
  //
  // Sprint en Uitdaging blijven zichtbaar zolang er nog niets bekend is; ze
  // krijgen alleen de voorwaarde erbij. Verbergen zou de pagina rustiger maken,
  // maar dan ziet het kind niet wat er te bereiken valt én verandert de pagina
  // later onaangekondigd van vorm — vier kaarten waar er eerst twee stonden,
  // precies op het moment dat er iets te vieren is.
  const {
    isEmpty: hasNoEvidence
  } = useLabEvidence();
  const getNotYetReason = (modeId: string): string | undefined => {
    if (!hasNoEvidence) return undefined;
    if (modeId === 'sprint') return 'Oefen eerst een onderwerp';
    if (modeId === 'challenge') return 'Eerst een onderwerp goed leren';
    return undefined;
  };

  // Beschikbaar nu vs later. `getEnabledModes()` levert alleen modi die in het
  // product bestaan; een uitgeschakelde modus komt hier dus niet voor. De split
  // gaat daarom over de voorwaarde die aan HET KIND hangt.
  const primaryModes = allModes.filter(m => !getNotYetReason(m.id));
  const secondaryModes = allModes.filter(m => !!getNotYetReason(m.id));

  // ── Derived state ──
  const pendingChallengeMastery = pendingChallengeFamilyId ? state.familyMastery?.[getFamilyMasteryKey(pendingChallengeFamilyId, selectedGroup)]?.masteryScore ?? 0 : 0;
  // ── FamilyPicker full-screen view ──
  if (familyPickerOpen) {
    return <FamilyPicker onClose={handleFamilyPickerClose} onSelect={handleFamilySelect} mode={familyPickerMode} data-id="element-3057" data-mp="2ws4vs0akyp8u" />;
  }
  // ── Main view ──
  const DAY_MS = 24 * 60 * 60 * 1000;
  return <PageContainer className="space-y-8 pb-20" data-id="element-3058" data-mp="2rrgv0mwjuu1f">
      {showCelebration && <ConfettiCelebration particleCount={100} duration={3000} data-id="element-3059" data-mp="35m9jiauf3kk5" />}

      <NewlyUnlockedBanner familyId={newlyUnlockedFamilyId} onClick={handleNewlyUnlockedClick} onClose={() => setNewlyUnlockedFamilyId(null)} data-id="element-3060" data-mp="30tq5jcrtinet" />
      

      {/* Continue Strip */}
      {lastSession && Date.now() - lastSession.timestamp < 7 * DAY_MS && <motion.div variants={itemVariants} data-id="element-3061" data-mp="3ajjd82ouaids">
          <ContinueStrip modeId={lastSession.modeId} familyId={lastSession.familyId} score={lastSession.score} timestamp={lastSession.timestamp} onContinue={() => {
        if (lastSession.familyId) {
          onStartMode?.(lastSession.modeId, {
            targetFamilyId: lastSession.familyId
          });
        } else {
          handleModeClick(lastSession.modeId);
        }
      }} data-id="element-3062" data-mp="3ok2lnax7vqgh" />
        
        </motion.div>}

      {/* Compact Hero + Mission Area */}
      <motion.div variants={itemVariants} data-id="element-3063" data-mp="1g2diw2fqpo1i">
        <LabHero labWelcome={labWelcome} selectedVariant={selectedVariant} allModesLength={allModes.length} onModeClick={handleModeClick} data-id="element-3064" data-mp="1k1us96yaf5by" />
        
      </motion.div>

      {/* Lab Stations Grid */}
      <motion.div variants={itemVariants} data-id="element-3065" data-mp="1b8hrjitzezfb">
        <WorldZone icon={Beaker} title="Hoe wil je oefenen?" accentColor="bg-fuchsia-100 text-fuchsia-600" djayMessage="Kies een manier om te oefenen! 🧪" djayEmotion="excited" isLast={true} data-id="element-3066" data-mp="1b76j35pld3hv">
          
          {/* Twee lagen in plaats van vier gelijke vlakken.
               Wat het kind NU kan doen krijgt de volle kaart; wat er nog niet
               inzit of nog niet bestaat, wordt een lichte rij eronder. De split
               volgt uit de staat (isEnabled + notYetReason), niet uit een
               voorkeur — dus zodra een kind een onderwerp geoefend heeft,
               schuift Sprint zelf naar boven.
                Twee kolommen in plaats van drie: met vier modi liet drie
               kolommen altijd één kaart alleen op de tweede rij achter, wat
               leest als een fout in plaats van als een keuze. */
        }
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3067" data-mp="2b8bfmww0y7ek">
            {primaryModes.map((mode, index) => {
            const enrichment = getEnrichmentForMode(mode.id);
            return <motion.div key={mode.id} initial={{
              opacity: 0,
              y: 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1],
              delay: Math.min(index * 0.04, 0.12)
            }} data-id="element-3068" data-mp="33ya8729fgmnz" data-mpi={index}>
                  
                  <LabStation mode={mode} statLabel={enrichment.statLabel} masteryPercent={enrichment.masteryPercent} personalRecord={enrichment.personalRecord} newlyUnlocked={mode.id === 'challenge' && !!newlyUnlockedFamilyId} onClick={() => handleModeClick(mode.id)} data-id="element-3069" data-mp="2p8m8g5gw9w2d" />
                  
                </motion.div>;
          })}
          </div>

          {secondaryModes.length > 0 && <div className="mt-6" data-id="element-3070" data-mp="3u8kdtbro62sw">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3" data-id="element-3071" data-mp="3u9vkpkgevqo8">
                Straks ook mogelijk
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-3072" data-mp="81m5ui44j5cl">
                {secondaryModes.map(mode => <LabStation key={mode.id} mode={mode} variant="compact" notYetReason={getNotYetReason(mode.id)} onClick={() => handleModeClick(mode.id)} data-id="element-3073" data-mp="c14qdcs19eew" />)}
              </div>
            </div>}
        </WorldZone>
      </motion.div>

      {/* ChallengeAcceptModal */}
      {pendingChallengeFamilyId && <ChallengeAcceptModal isOpen={challengeModalOpen} familyId={pendingChallengeFamilyId} masteryScore={pendingChallengeMastery} onAccept={handleChallengeAccept} onDecline={handleChallengeDecline} data-id="element-3074" data-mp="qrc116bstt51" />}

      {/* Unified ModeIntroSheet */}
      {introSheetMode && <ModeIntroSheet isOpen={!!introSheetMode} modeId={introSheetMode} onStart={handleIntroStart} onCancel={handleIntroCancel} isColdStart={introSheetMode === 'fluency' ? isFluencyColdStart : false} data-id="element-3075" data-mp="zpe5xwynl5xl" />}

      {/* FluencyModulePicker Modal */}
      <FluencyModulePicker isOpen={fluencyModulePickerOpen} onSelectAll={() => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency');
    }} onSelectModule={moduleId => {
      setFluencyModulePickerOpen(false);
      onStartMode?.('fluency', {
        targetModules: [moduleId]
      });
    }} onCancel={() => setFluencyModulePickerOpen(false)} data-id="element-3076" data-mp="1uztuuboeac9t" />
      
    </PageContainer>;
}